import type { RouteRecordRaw } from "vue-router"
import { markRaw } from "vue"
import { injectRouteList } from "@/hooks/useRouter"
import LevelTwoRouter from "@/layout/components/NavigationBar/LevelTwoRouter.vue"
import {
  DASHBOARD_ACTION_PERMISSIONS,
  DASHBOARD_REPORT_PERMISSIONS,
  type PermissionRequirement,
  SETTING_ACTION_PERMISSIONS,
} from "@/utils/roleFeatureAccess"

const Layout = () => import("@/layout/index.vue")
const BillingRuleManage = () => import("@/views/page/setting/components/BillingRuleManage/BillingRuleManage.vue")

const PhoneProductManage = () => import("@/views/page/setting/components/ProductByPhone/PhoneProductManage.vue")
const ShopManage = () => import("@/views/page/setting/components/ProductByPhone/ShopManage.vue")
const ShopOrder = () => import("@/views/page/setting/components/ProductByPhone/ShopOrder.vue")
const WorkerManage = () => import("@/views/page/setting/components/WorkerManage/WorkerManage.vue")
const RoleManage = () => import("@/views/page/setting/components/WorkerManage/RoleManage.vue")
// const CommissionManage = () => import("@/views/page/setting/components/WorkerManage/CommissionManage.vue")
// const CreateCommission = () => import("@/views/page/setting/components/WorkerManage/CreateCommission.vue")
const PrintManage = () => import("@/views/page/setting/DeviceManage/PrintManage.vue")
const DiscountSettings = () => import("@/views/page/setting/DiscountSettings/DiscountSettings.vue")
const MessageManage = () => import("@/views/page/setting/MessageManage/MessageManage.vue")
const VoiceBroadcastTest = () => import("@/views/page/setting/VoiceBroadcastTest/VoiceBroadcastTest.vue")
// const QrCodeManage = () => import("@/views/page/setting/MessageManage/MessageManage.vue")
const TableManege = () => import("@/views/page/setting/components/TableManege/TableManege.vue")
const PayMethodsManage = () => import("@/views/page/setting/components/PayMethodsManage/PayMethodsManage.vue")
const CustomizeBusinessDates = () =>
  import("@/views/page/setting/components/PayMethodsManage/CustomizeBusinessDates.vue")
const ProductManage = () => import("@/views/page/setting/components/ProductManage/ProductManage.vue")
const UnitManage = () => import("@/views/page/setting/components/ProductManage/UnitManage.vue")
const EmployeeCommissions = () => import("@/views/page/setting/components/WorkerManage/EmployeeCommissions.vue")
const voiceBroadcastTestPermission = "setting_other_voice_broadcast_test"
const OperationApprovalTickets = () => import("@/views/page/dashboard/components/SensitiveApproval/SensitiveApprovalTickets.vue")
const OperationApprovalConfig = () => import("@/views/page/dashboard/components/SensitiveApproval/SensitiveApprovalConfig.vue")

const toPermissionArray = (permissions: PermissionRequirement) => Array.isArray(permissions) ? [...permissions] : [permissions]
const operationCenterPermissions = [
  ...toPermissionArray(DASHBOARD_REPORT_PERMISSIONS.sensitiveOperations),
  ...toPermissionArray(DASHBOARD_ACTION_PERMISSIONS.sensitiveAudit),
  ...toPermissionArray(DASHBOARD_ACTION_PERMISSIONS.sensitiveConfig),
]

const payMethodManageRoute: RouteRecordRaw[] = [
  {
    path: "/setting/pay-method-manage/pay-method",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/pay-method-manage/pay-method/index",
    meta: {
      title: "收款方式管理",
      hidden: true,
      permissions: ["setting_payment_mode"],
    },
    children: [
      {
        path: "/setting/pay-method-manage/pay-method/index",
        name: "PayMethodsManage",
        component: PayMethodsManage,
        meta: {
          title: "收款方式管理",
          hidden: true,
          permissions: ["setting_payment_mode"],
        },
      },
    ],
  },
  {
    path: "/setting/pay-method-manage/day-end",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/pay-method-manage/day-end/index",
    meta: {
      title: "交接班/日结",
      hidden: true,
      permissions: ["setting_payment_handover"],
    },
    children: [
      {
        path: "/setting/pay-method-manage/day-end/index",
        component: CustomizeBusinessDates,
        meta: {
          title: "交接班/日结",
          hidden: true,
          permissions: ["setting_payment_handover"],
        },
      },
    ],
  },
]

const workerManageRoute: RouteRecordRaw[] = [
  {
    path: "/setting/worker-manage/worker-manage",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/worker-manage/worker-manage/index",
    meta: {
      title: "员工管理",
      hidden: true,
      permissions: ["setting_worker"],
    },
    children: [
      {
        path: "/setting/worker-manage/worker-manage/index",
        name: "WorkerManage",
        component: WorkerManage,
        meta: {
          title: "员工管理",
          hidden: true,
          permissions: ["setting_worker"],
        },
      },
    ],
  },
  {
    path: "/setting/worker-manage/role-manage",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/worker-manage/role-manage/index",
    meta: {
      title: "角色管理",
      hidden: true,
      permissions: ["setting_worker_all"],
    },
    children: [
      {
        path: "/setting/worker-manage/role-manage/index",
        name: "RoleManage",
        component: RoleManage,
        meta: {
          title: "角色管理",
          hidden: true,
          permissions: ["setting_worker_all"],
        },
      },
    ],
  },
  {
    path: "/setting/worker-manage/employee-commissions",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/worker-manage/employee-commissions/index",
    meta: {
      title: "员工提成",
      hidden: true,
      permissions: [SETTING_ACTION_PERMISSIONS.manageWorkerCommissionRules],
    },
    children: [
      {
        path: "/setting/worker-manage/employee-commissions/index",
        name: "EmployeeCommissions",
        component: EmployeeCommissions,
        meta: {
          title: "员工提成",
          hidden: true,
          permissions: [SETTING_ACTION_PERMISSIONS.manageWorkerCommissionRules],
        },
      },
    ],
  },

  // {
  //   path: "/setting/worker-manage/commission-manage",
  //   component: markRaw(LevelTwoRouter),
  //   redirect: "/setting/worker-manage/commission-manage/index",
  //   meta: {
  //     title: "员工提成管理",
  //     hidden: true,
  //     permissions: ["setting_worker_all"],
  //   },
  //   children: [
  //     {
  //       path: "/setting/worker-manage/commission-manage/index",
  //       name: "CommissionManage",
  //       component: CommissionManage,
  //     },
  //     {
  //       path: "/setting/worker-manage/commission-manage/create/:commissionType",
  //       name: "CreateCommission",
  //       component: CreateCommission,
  //       props: true,
  //       meta: {
  //         title: "创建提成方案",
  //         hidden: true,
  //         // permissions: ["setting_worker_all"],
  //       },
  //     },
  //   ],
  // },
]

const operationCenterRoute: RouteRecordRaw[] = [
  {
    path: "/setting/operation-center/operation-approval",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/operation-center/operation-approval/index",
    meta: {
      title: "操作审核",
      hidden: true,
      permissions: DASHBOARD_ACTION_PERMISSIONS.sensitiveAudit,
    },
    children: [
      {
        path: "/setting/operation-center/operation-approval/index",
        name: "OperationApprovalTickets",
        component: OperationApprovalTickets,
        meta: {
          title: "操作审核",
          hidden: true,
          permissions: DASHBOARD_ACTION_PERMISSIONS.sensitiveAudit,
        },
      },
    ],
  },
  {
    path: "/setting/operation-center/operation-config",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/operation-center/operation-config/index",
    meta: {
      title: "操作配置",
      hidden: true,
      permissions: DASHBOARD_ACTION_PERMISSIONS.sensitiveConfig,
    },
    children: [
      {
        path: "/setting/operation-center/operation-config/index",
        name: "OperationApprovalConfig",
        component: OperationApprovalConfig,
        meta: {
          title: "操作配置",
          hidden: true,
          permissions: DASHBOARD_ACTION_PERMISSIONS.sensitiveConfig,
        },
      },
    ],
  },
]

const shopManageRoute: RouteRecordRaw[] = [
  {
    path: "/setting/online-shop/phone-product",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/online-shop/phone-product/index",
    meta: {
      title: "商品管理",
      hidden: true,
      permissions: ["setting_phone_product"],
    },
    children: [
      {
        path: "/setting/online-shop/phone-product/index",
        name: "PhoneProductManage",
        component: PhoneProductManage,
        meta: {
          title: "商品管理",
          hidden: true,
          permissions: ["setting_phone_product"],
        },
      },
    ],
  },
  {
    path: "/setting/online-shop/shop-manage",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/online-shop/shop-manage/index",
    meta: {
      title: "店铺信息",
      hidden: true,
      permissions: ["setting_shop_manage"],
    },
    children: [
      {
        path: "/setting/online-shop/shop-manage/index",
        name: "ShopManage",
        component: ShopManage,
        meta: {
          title: "店铺信息",
          hidden: true,
          permissions: ["setting_shop_manage"],
        },
      },
    ],
  },
  {
    path: "/setting/online-shop/shop-order",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/online-shop/shop-order/index",
    meta: {
      title: "线上预订",
      hidden: true,
      scopeTypes: ["chain-store"],
      permissions: ["setting_shop_order"],
    },
    children: [
      {
        path: "/setting/online-shop/shop-order/index",
        name: "ShopOrder",
        component: ShopOrder,
        meta: {
          title: "线上预订",
          hidden: true,
          scopeTypes: ["chain-store"],
          permissions: ["setting_shop_order"],
        },
      },
    ],
  },
]

const otherManageRoute: RouteRecordRaw[] = [
  {
    path: "/setting/other/business-settings",
    name: "StoreBusinessSettings",
    component: () => import("@/views/page/setting/BusinessSettings/StoreBusinessSettings.vue"),
    meta: { title: "门店功能与提醒", permissions: ["setting_shop_manage"] },
  },
  {
    path: "/setting/other/message-manage",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/other/message-manage/index",
    meta: {
      title: "短信管理",
      hidden: true,
      permissions: ["setting_other_message"],
    },
    children: [
      {
        path: "/setting/other/message-manage/index",
        name: "MessageManage",
        component: MessageManage,
        meta: {
          title: "短信管理",
          hidden: true,
          permissions: ["setting_other_message"],
        },
      },
    ],
  },
  {
    path: "/setting/other/voice-broadcast-test",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/other/voice-broadcast-test/index",
    meta: {
      title: "语音播报测试",
      hidden: true,
      permissions: [voiceBroadcastTestPermission],
    },
    children: [
      {
        path: "/setting/other/voice-broadcast-test/index",
        name: "VoiceBroadcastTest",
        component: VoiceBroadcastTest,
        meta: {
          title: "语音播报测试",
          hidden: true,
          permissions: [voiceBroadcastTestPermission],
        },
      },
    ],
  },
  {
    path: "/setting/other/unit-manage",
    redirect: "/setting/other/unit-manage/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "单位管理",
      hidden: true,
      permissions: ["setting_product_unit"],
    },
    children: [
      {
        path: "/setting/other/unit-manage/index",
        component: UnitManage,
        meta: {
          title: "单位管理",
          hidden: true,
          permissions: ["setting_product_unit"],
        },
      },
    ],
  },
  {
    path: "/setting/other/device-manage",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/other/device-manage/index",
    meta: {
      title: "设备管理",
      hidden: true,
      permissions: ["setting_other_printer"],
    },
    children: [
      {
        path: "/setting/other/device-manage/index",
        name: "PrintManage",
        component: PrintManage,
        meta: {
          title: "设备管理",
          hidden: true,
          permissions: ["setting_other_printer"],
        },
      },
    ],
  },
  {
    path: "/setting/other/discount-settings",
    component: markRaw(LevelTwoRouter),
    redirect: "/setting/other/discount-settings/index",
    meta: {
      title: "优惠设置",
      hidden: true,
      permissions: ["setting_other_discount"],
    },
    children: [
      {
        path: "/setting/other/discount-settings/index",
        name: "DiscountSettings",
        component: DiscountSettings,
        meta: {
          title: "优惠设置",
          hidden: true,
          permissions: ["setting_other_discount"],
        },
      },
    ],
  },
  //   {
  //     path: "/setting/other/qrcode-manage",
  //     component: markRaw(LevelTwoRouter),
  //     redirect: "/setting/other/qrcode-manage/index",
  //     meta: {
  //       title: "二维码管理",
  //       hidden: true
  //     },
  //     children: [
  //       {
  //         path: "/setting/other/qrcode-manage/index",
  //         name: "QrCodeManage",
  //         component: QrCodeManage,
  //         meta: {
  //           title: "二维码管理",
  //           hidden: true
  //         }
  //       }
  //     ]
  //   }
]

const firstSettingsLevelRoute: RouteRecordRaw[] = [
  {
    path: "/setting/product-manage",
    redirect: "/setting/product-manage/index",
    meta: {
      title: "商品管理",
      permissions: ["setting_product"],
      activeMenu: '/setting/product-manage',
    },
    children: [
      {
        path: "/setting/product-manage/index",
        name: "ProductManage",
        component: ProductManage,
        meta: {
          title: "商品管理",
          hidden: true,
          permissions: [SETTING_ACTION_PERMISSIONS.manageProducts, SETTING_ACTION_PERMISSIONS.manageProductSales],
        },
      },
    ],
  },
  {
    path: "/setting/table-manage",
    redirect: "/setting/table-manage/index",
    meta: {
      title: "桌台管理",
      permissions: ["setting_table"],
      activeMenu: '/setting/table-manage',
    },
    children: [
      {
        path: "/setting/table-manage/index",
        name: "TableManege",
        component: TableManege,
        meta: {
          title: "桌台管理",
          hidden: true,
          permissions: [SETTING_ACTION_PERMISSIONS.manageTables, SETTING_ACTION_PERMISSIONS.viewTableQrCode],
        },
      },
    ],
  },
  {
    path: "/setting/billing-rule-manage",
    redirect: "/setting/billing-rule-manage/index",
    meta: {
      title: "计费规则管理",
      permissions: ["setting_bill_rule"],
      activeMenu: '/setting/billing-rule-manage',
    },
    children: [
      {
        path: "/setting/billing-rule-manage/index",
        name: "BillingRuleManage",
        component: BillingRuleManage,
        meta: {
          title: "计费规则管理",
          hidden: true,
          permissions: ["setting_bill_rule_all"],
        },
      },
    ],
  },
  {
    path: "/setting/operation-center",
    redirect: "/setting/operation-center/operation-approval",
    meta: {
      title: "操作中心",
      permissions: operationCenterPermissions,
      activeMenu: '/setting/operation-center',
    },
    children: injectRouteList(operationCenterRoute),
  },
  {
    path: "/setting/worker-manage",
    redirect: "/setting/worker-manage/worker-manage",
    meta: {
      title: "员工管理",
      permissions: ["setting_worker"],
      activeMenu: '/setting/worker-manage',
    },
    children: injectRouteList(workerManageRoute),
  },
  {
    path: "/setting/pay-method-manage",
    redirect: "/setting/pay-method-manage/pay-method",
    name: "PayMethodLayout",
    meta: {
      title: "收银基础设置",
      permissions: ["setting_payment"],
      activeMenu: '/setting/pay-method-manage',
    },
    children: injectRouteList(payMethodManageRoute),
  },
  {
    path: "/setting/online-shop",
    redirect: "/setting/online-shop/phone-product",
    meta: {
      title: "线上店铺管理",
      permissions: ["setting_phone"],
      activeMenu: '/setting/online-shop',
    },
    children: injectRouteList(shopManageRoute),
  },
  {
    path: "/setting/other",
    redirect: "/setting/other/message-manage",
    meta: {
      title: "其他设置",
      activeMenu: '/setting/other',
    },
    children: injectRouteList(otherManageRoute),
  },
] as const

const setting: RouteRecordRaw[] = [
  {
    path: "/setting",
    component: Layout,
    redirect: "/setting/product-manage",
    children: injectRouteList(firstSettingsLevelRoute),
    meta: {
      title: "设置",
      showTag: false,
      svgIcon: "nav-setting",
      order: 6,
      permissions: ["setting"],
    },
  },
]
export default setting
